import React, { useState, type FC, useEffect } from 'react'
import Grid from '@mui/material/Grid'
import { viewDeviceService } from '../../services/viewDeviceService'
import { Tooltip, CircularProgress, Box } from '@mui/material'
import LanguageStrings from '../../../../i18n/locales'
import './ViewDevice.css'
import moment from 'moment'
import { MemorizedParametersTable } from './ParametersTable'
import { ZoomControls } from '../../../common/WraperHierarchy'
import HierarchyView from '../../../common/HierarchyView'

interface ViwDevice {
  loading: boolean
  ViewDevice: any
  errorMsg: boolean
}
const ViewDeviceStrings = LanguageStrings().DeviceManagementCollectionStrings?.ViewDevice

const ViewDevice: FC<any> = (props) => {
  const [deviceView, setdeviceView] = useState<ViwDevice>({
    loading: false,
    ViewDevice: {},
    errorMsg: false
  })
  const [getParametersData, setParametersData] = useState<any[]>([])
  const [stdIndicators, setStdIndicators] = useState<any[]>([])
  const [customIndicators, setCustomIndicators] = useState<any[]>([])
  const [parametersLoading, setParametersLoading] = useState<boolean>(true)
  const [kpiLoading, setKpiLoading] = useState<boolean>(true)
  const columns = [
    ViewDeviceStrings.ModbusRegistered,
    ViewDeviceStrings.ParameterName,
    ViewDeviceStrings.ExecutionFrequency,
    ViewDeviceStrings.LowerAlarm,
    ViewDeviceStrings.LowLimit,
    `${ViewDeviceStrings.Operator} (Lower)`, // CHANGED: Made unique
    ViewDeviceStrings.LowSeverity,
    ViewDeviceStrings.LowerColor,
    ViewDeviceStrings.LowerText,
    ViewDeviceStrings.LowerLabel,
    ViewDeviceStrings.HigherAlarm,
    ViewDeviceStrings.HighLimit,
    `${ViewDeviceStrings.Operator} (Higher)`, // CHANGED: Made unique
    ViewDeviceStrings.HigherSeverity,
    ViewDeviceStrings.HigherColor,
    ViewDeviceStrings.HigherText,
    ViewDeviceStrings.HigherLabel
  ]
  const [flowTreeInstance, setFlowTreeInstance] = useState<any>()
  const [currentZoom, setCurrentZoom] = useState<number>(0)

  const handleInstance = React.useCallback((instance: any) => {
    setFlowTreeInstance(instance)
    setCurrentZoom(instance.getZoom())
  }, [])

  const handleZoomChange = React.useCallback((zoom: number) => {
    setCurrentZoom(zoom)
  }, [])

  useEffect(() => {
    viewDeviceData()
  }, [props.deviceId])

  const viewDeviceData = (): any => {
    setParametersLoading(true)
    setKpiLoading(true)
    viewDeviceService.viewDevice(props.deviceId)
      .then((res: any) => {
        setdeviceView({ ...deviceView, ViewDevice: res.data.data })
        getModbusParametersData(res.data.data.device_type_id)
        getKpiData(res.data.data.device_type_id)
      })
      .catch((res: any) => {
        // Errors
      })
  }
  const getModbusParametersData = (assetTypeId: string): void => {
    viewDeviceService.getDeviceParamsConfig(props.deviceId, assetTypeId)
      .then((res: any) => {
        const enabledData = res.data.data.filter((res: any) => res.isRegisterEnabled === true && res.isThisFromDefaultConfig === false)

        const displayData = enabledData.map((res: any) => {
          return [res?.registerAddress,
            res?.parameterName ?? '-',
            formatExecutionFrequency(res?.pollingIntervalMinutes ?? 0),
            res?.isLowerLimitAlarmEnabled === true ? 'Enabled' : 'Disabled',
            res.alarmLowerLimit ?? '-',
            res?.lowerLimitOperator ?? '-',
            res?.alarmLowerLimitSeverityLevel ?? '-',
            res?.alarmLowerLimitColorName ?? '-',
            res?.alarmLowerLimitStateText ?? '-',
            res?.alarmLowerLimitLabelName ?? '-',
            res?.isHigherLimitAlarmEnabled === true ? 'Enabled' : 'Disabled',
            res?.alarmHigherLimit ?? '-',
            res?.higherLimitOperator ?? '-',
            res?.alarmHigherLimitSeverityLevel ?? '-',
            res?.alarmHigherLimitColorName ?? '-',
            res?.alarmHigherLimitStateText ?? '-',
            res?.alarmHigherLimitLabelName ?? '-'
          ]
        })
        setParametersData(displayData)
        setParametersLoading(false)
      })
      .catch((res: any) => {
        setParametersLoading(false)
        // Error
      })
  }
  const getKpiData = (assetTypeId: string): void => {
    viewDeviceService.getPerformanceIndicators(props.deviceId, assetTypeId)
      .then((res: any) => {
        const kpiIndicators = res.data.data
        const stdIndicator = kpiIndicators.filter((indicator: any) => indicator.isKpiEnabled === true && indicator.isCustomKpi === false && indicator.isDefaultConfig === false)
        const modifyStdIndicator = stdIndicator.map((res: any) => {
          return [
            res?.kpiName ?? '-',
            ' ',
            String(res?.executionFrequencyMinutes ?? '0') + ' minutes',
            res?.isLowerLimitAlarmEnabled === true ? 'Enabled' : 'Disabled',
            res?.alarmLowerLimit ?? '-',
            res?.lowerLimitOperator ?? '-',
            res?.alarmLowerLimitSeverityLevel ?? '-',
            res?.alarmLowerLimitColorName ?? '-',
            res?.alarmLowerLimitStateText ?? '-',
            res?.alarmLowerLimitLabelName ?? '-',
            res?.isHigherLimitAlarmEnabled === true ? 'Enabled' : 'Disabled',
            res?.alarmHigherLimit ?? '-',
            res?.higherLimitOperator ?? '-',
            res?.alarmHigherLimitSeverityLevel ?? '-',
            res?.alarmHigherLimitColorName ?? '-',
            res?.alarmHigherLimitStateText ?? '-',
            res?.alarmHigherLimitLabelName ?? '-'
          ]
        })
        setStdIndicators(modifyStdIndicator)

        const customIndicator = kpiIndicators.filter((indicator: any) => indicator.isKpiEnabled === true && indicator.isCustomKpi === true && indicator.isDefaultConfig === false)
        const modifyCustomIndicator = customIndicator.map((res: any) => {
          return [
            res?.kpiName ?? '-',
            ' ',
            String(res?.executionFrequencyMinutes ?? '0') + ' minutes',
            res?.isLowerLimitAlarmEnabled === true ? 'Enabled' : 'Disabled',
            res?.alarmLowerLimit ?? '-',
            res?.lowerLimitOperator ?? '-',
            res?.alarmLowerLimitSeverityLevel ?? '-',
            res?.alarmLowerLimitColorName ?? '-',
            res?.alarmLowerLimitStateText ?? '-',
            res?.alarmLowerLimitLabelName ?? '-',
            res?.isHigherLimitAlarmEnabled === true ? 'Enabled' : 'Disabled',
            res?.alarmHigherLimit ?? '-',
            res?.higherLimitOperator ?? '-',
            res?.alarmHigherLimitSeverityLevel ?? '-',
            res?.alarmHigherLimitColorName ?? '-',
            res?.alarmHigherLimitStateText ?? '-',
            res?.alarmHigherLimitLabelName ?? '-'
          ]
        })
        setCustomIndicators(modifyCustomIndicator)
      })
      .catch((res: any) => {
        // Error
      })
      .finally(() => {
        setKpiLoading(false)
      })
  }
  const isAllDataLoaded = (): boolean => {
    return !parametersLoading && !kpiLoading
  }
  const formatTextWithTooltip = (text: string): JSX.Element => {
    if (text?.length > 20) {
      return (<Tooltip title={text}><span>{`${text.substring(0, 20)}...`}</span></Tooltip>)
    }
    return <span>{text !== '' ? text : '-'}</span>
  }

  const formatExecutionFrequency = (seconds: number): string => {
    if (seconds === 60) {
      return '1 minute'
    } else if (seconds < 60) {
      return `${seconds} seconds`
    } else {
      // For other cases, convert to minutes if it's a clean division
      const minutes = seconds / 60
      if (minutes === Math.floor(minutes)) {
        return `${minutes} minute${minutes > 1 ? 's' : ''}`
      } else {
        return `${seconds} seconds`
      }
    }
  }
  const { ViewDevice } = deviceView

  return (
     <Grid container justifyContent={'space-between'} display={'flex'} alignItems={'center'} sx={{
       marginTop: 2,
       paddingRight: 1,
       height: '90%',
       overflow: 'auto'
     }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#3B3B3B',
          padding: 2,
          borderRadius: 1,
          color: '#f0f0f0',
          marginTop: 2,
          width: '100%',
          fontFamily: 'Honeywell Sans Web',
          border: '1px solid #000000'
        }}
      >
       <Grid item xs={12}>
         <Grid container className="view_device_popup_content" spacing={2}>
           <Grid item xs={6} md={4} lg={3} >
             <p className="view_device_popup_firstrow_title">{ViewDeviceStrings.DeviceName}</p>
             <p className="view_device_popup_firstrow_desc">{formatTextWithTooltip(ViewDevice.name)}</p>
           </Grid>
           <Grid item xs={6} md={4} lg={3} >
             <p className="view_device_popup_firstrow_title">{ViewDeviceStrings.SerialNumber}</p>
             <p className="view_device_popup_firstrow_desc">{ViewDevice?.serial_number != null && ViewDevice?.serial_number !== '' ? ViewDevice?.serial_number : '-'}</p>
           </Grid>
           <Grid item xs={6} md={4} lg={3} >
             <p className="view_device_popup_firstrow_title">{ViewDeviceStrings.SlaveAddress}</p>
             <p className="view_device_popup_firstrow_desc">{ViewDevice?.slave_id !== '' ? ViewDevice?.slave_id : '-'}</p>
           </Grid>
            {ViewDevice?.port != null && ViewDevice?.port !== 'null' && (
            <Grid item xs={6} md={4} lg={3} >
              <p className="view_device_popup_firstrow_title">{ViewDeviceStrings.PortNumber}</p>
              <p className="view_device_popup_firstrow_desc">{(ViewDevice?.port !== '' && ViewDevice?.port !== 'null') ? ViewDevice?.port : '-'}</p>
            </Grid>
            )}
           <Grid item xs={6} md={4} lg={3} >
             <p className="view_device_popup_firstrow_title">{ViewDeviceStrings.CreatedOn}</p>
             <p className="view_device_popup_firstrow_desc">
               {ViewDevice?.install_date !== null && ViewDevice?.install_date !== '' ? moment(ViewDevice?.install_date).format('hh:mm A, MMM DD YYYY') : '-'}
             </p>
           </Grid>
         </Grid>
       </Grid>
      </Box>
      <Grid item xs={12} >
        {parametersLoading
          ? <div className="loader-container"><CircularProgress sx={{ color: 'black' }} data-testid="parameters-loading" /></div>
          : <MemorizedParametersTable className='alarmDeviceLevel' testid="render-data-lists" title="Other Parameters" columns={columns} rows={getParametersData} freezColumns={3} />
        }
      </Grid>
      <Grid item xs={12} >
      {kpiLoading
        ? <div className="loader-container"><CircularProgress sx={{ color: 'black' }} data-testid="kpi-loading" /></div>
        : (<>
          {ViewDevice?.device_type === 'Thermal-Controller-7800' && <MemorizedParametersTable className='alarmDeviceLevel' testid="standard-key-performance" title="Standard KPIs" columns={[...columns.slice(0, 1), '', ...columns.slice(2)]} rows={stdIndicators} freezColumns={3} /> }
          <MemorizedParametersTable className='alarmDeviceLevel' testid="custom-key-performance" title="Custom KPIs" columns={[...columns.slice(0, 1), '', ...columns.slice(2)]} rows={customIndicators} freezColumns={3} />
        </>)
      }
    </Grid>
    <Grid item xs={12} className="device_information">
        <p><u>{ViewDeviceStrings.HierarchyView}</u></p>
    </Grid>
    <Grid item container display={'inline-flex'} justifyContent={'center'} >
      <Grid item xs={12} sx={{ height: '300px' }} >
        {!isAllDataLoaded()
          ? <div className="loader-container"><CircularProgress sx={{ color: 'black' }} data-testid="hierarchy-loading" /></div>
          : <HierarchyView site={ViewDevice?.hierarchyStructure?.site} id={props.deviceId} setInstance={handleInstance} onZoomChange={handleZoomChange} defaultViewportX={window.innerWidth / 4} />
        }
      </Grid>
    </Grid>
    <Grid item xs={12} >
      <ZoomControls flowTreeInstance={flowTreeInstance} currentZoom={currentZoom} defaultViewportX={window.innerWidth / 4} />
    </Grid>
    </Grid>
  )
}

export default ViewDevice
